import { z } from "zod";
import { assetKeySchema } from "./primitives.js";

/**
 * The fonts a card can be set in.
 *
 * There were five, and they were five because the render container had
 * `fonts-liberation` and `fonts-dejavu-core` installed and nothing else:
 * naming Georgia was naming a face that would be substituted. The library now
 * ships with the application as ordinary dependencies, so the editor and the
 * encoder draw the same letters and neither reaches the network to do it —
 * which matters, because the container has no outbound network at all.
 *
 * Every face here is OFL-licensed, the licence that permits shipping a font
 * inside an application. That is not a detail: it is the reason this route
 * exists rather than a download-on-demand one.
 */

/** One family in the bundled library. */
export interface BookendFont {
  /** Stable identifier. Not stored — `stack` is what a card carries. */
  id: string;
  /** How the picker names it, and the family the bundle registers. */
  family: string;
  category: BookendFontCategory;
  /**
   * What gets written to a card. The bundled family first and real fallbacks
   * after it, so a browser that failed to load the face still draws words.
   */
  stack: string;
  /** The weights the bundle actually carries, ascending. */
  weights: number[];
  /** Whether a true italic is bundled; false means the browser slants it. */
  italic: boolean;
}

export type BookendFontCategory = "Sans" | "Serif" | "Mono" | "Display";

/** The order the picker groups by — familiar first, decorative last. */
export const bookendFontCategories = ["Sans", "Serif", "Mono", "Display"] as const;

const SANS = 'Inter, "Liberation Sans", Arial, system-ui, sans-serif';
const SERIF = '"Liberation Serif", Georgia, "Times New Roman", serif';
const MONO = '"Liberation Mono", "SF Mono", Menlo, "Courier New", monospace';

const font = (
  id: string,
  family: string,
  category: BookendFontCategory,
  fallback: string,
  weights: number[],
  italic: boolean,
): BookendFont => ({ id, family, category, stack: `"${family}", ${fallback}`, weights, italic });

export const bookendFonts: readonly BookendFont[] = [
  // Inter keeps the exact stack cards have always stored, so every card written
  // before the library existed matches an entry rather than falling off the end
  // of the picker — and starts rendering in the real face instead of Liberation.
  { id: "inter", family: "Inter", category: "Sans", stack: SANS, weights: [400, 500, 600, 700, 800], italic: true },
  font("dm-sans", "DM Sans", "Sans", SANS, [400, 500, 700, 800], true),
  font("work-sans", "Work Sans", "Sans", SANS, [400, 500, 700, 800], true),
  font("space-grotesk", "Space Grotesk", "Sans", SANS, [400, 500, 700], false),
  font("archivo", "Archivo", "Sans", SANS, [400, 500, 700, 800], true),
  font("manrope", "Manrope", "Sans", SANS, [400, 500, 700, 800], false),
  font("figtree", "Figtree", "Sans", SANS, [400, 500, 700, 800], true),
  font("outfit", "Outfit", "Sans", SANS, [400, 500, 700, 800], false),

  font("source-serif-4", "Source Serif 4", "Serif", SERIF, [400, 500, 700, 800], true),
  font("playfair-display", "Playfair Display", "Serif", SERIF, [400, 500, 700, 800], true),
  font("lora", "Lora", "Serif", SERIF, [400, 500, 700], true),
  font("libre-baskerville", "Libre Baskerville", "Serif", SERIF, [400, 500, 700], true),
  font("bitter", "Bitter", "Serif", SERIF, [400, 500, 700, 800], true),
  font("fraunces", "Fraunces", "Serif", SERIF, [400, 500, 700, 800], true),

  font("jetbrains-mono", "JetBrains Mono", "Mono", MONO, [400, 500, 700, 800], true),
  font("ibm-plex-mono", "IBM Plex Mono", "Mono", MONO, [400, 500, 700], true),
  font("space-mono", "Space Mono", "Mono", MONO, [400, 700], true),

  // Display faces ship one weight each. `nearestWeight` is what keeps a card
  // set in bold from asking for an 800 that does not exist and getting a
  // smeared synthetic one.
  font("bebas-neue", "Bebas Neue", "Display", SANS, [400], false),
  font("anton", "Anton", "Display", SANS, [400], false),
  font("instrument-serif", "Instrument Serif", "Display", SERIF, [400], true),
] as const;

/**
 * The family a CSS stack asks for first, unquoted.
 *
 * `document.fonts.load` wants a family name, not a stack, and the picker wants
 * one to preview with; both go through here so they cannot disagree.
 */
export function primaryFamily(stack: string): string {
  const first = stack.split(",")[0]?.trim() ?? "";
  return first.replace(/^["']|["']$/g, "").trim();
}

/** The library entry a stored stack refers to, or `undefined` if it predates the library. */
export function findBookendFont(stack: string | undefined): BookendFont | undefined {
  if (!stack) return undefined;
  const family = primaryFamily(stack);
  return bookendFonts.find((candidate) => candidate.family === family);
}

/**
 * The closest weight the bundle actually carries.
 *
 * A browser asked for 800 from a face that only has 400 does not decline; it
 * smears the 400 into a synthetic bold that looks nothing like the family.
 * Choosing the nearest real weight instead means the editor and the encoder
 * make the same choice, which is the only way the preview can be trusted.
 * Ties go to the lighter face, which is the one that stays readable.
 */
export function nearestWeight(weights: readonly number[], requested: number): number {
  if (weights.length === 0) return requested;
  return weights.reduce((best, weight) => (Math.abs(weight - requested) < Math.abs(best - requested) ? weight : best));
}

/**
 * A font someone uploaded, as the timeline carries it.
 *
 * On the timeline rather than looked up from the project, because the renderer
 * is handed a timeline and nothing else — a family it cannot resolve to a file
 * is a card drawn in the fallback with nothing to say why.
 */
export const projectFontSchema = z.object({
  /** The CSS family name, which is also what the picker shows. */
  family: z.string().min(1).max(80),
  assetKey: assetKeySchema,
});

export type ProjectFont = z.infer<typeof projectFontSchema>;

/** File types a font can be uploaded as. woff2 first; the rest are what people have. */
export const allowedFontMimeTypes = [
  "font/woff2",
  "font/woff",
  "font/ttf",
  "font/otf",
  "application/font-woff",
  "application/x-font-ttf",
  "application/x-font-otf",
  "application/octet-stream",
] as const;

/** Extensions accepted, checked as well as the mime type — browsers disagree about fonts. */
export const allowedFontExtensions = [".woff2", ".woff", ".ttf", ".otf"] as const;

/**
 * A family name a CSS stack can carry.
 *
 * Quotes and commas would end the family early and silently take the rest of
 * the stack with them, which is the sort of break that shows up as a card in
 * the wrong font three steps later.
 */
export function fontFamilyName(raw: string): string {
  const clean = raw
    .replace(/\.(woff2?|ttf|otf)$/i, "")
    .replace(/["',;]/g, " ")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 60);
  return clean || "Uploaded font";
}

/**
 * The stack written onto a card for an uploaded face.
 *
 * The bundled fallbacks stay behind it deliberately: an asset that fails to
 * load should leave readable words rather than nothing.
 */
export function uploadedFontStack(family: string): string {
  return `"${fontFamilyName(family)}", ${SANS}`;
}
